import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { OTP_SEND, OTP_VERIFIED } from "../../../constant-value";
import api from "../../../services/axiosConfig";

const SMSCode = ({ nextStep, prevStep, values }) => {
  const navigate = useNavigate();

  const [cooldown, setCooldown] = useState(0);
  const [errorShake, setErrorShake] = useState(false);

  const inputsRef = useRef([]);

  const [otpState, setOtpState] = useState({
    fullName: values.name,
    phoneNumber: values.mobileNumber,
    password: values.password,
    dateOfBirth: "2025-05-01T12:44:53.597Z",
    otpCode: ""
  });

  const reOtp = {
    phoneNumber: values.mobileNumber
  };

  // ---------------------------------------------------
  // 🚀 Web OTP API (Android Auto-Fill)
  // ---------------------------------------------------
  useEffect(() => {
    if ("OTPCredential" in window) {
      const ac = new AbortController();

      navigator.credentials
        .get({
          otp: { transport: ["sms"] },
          signal: ac.signal
        })
        .then((otp) => {
          if (!otp || !otp.code) return;

          const digits = otp.code.split("").slice(0, 4);

          digits.forEach((d, i) => {
            inputsRef.current[i].value = d;
          });

          setOtpState((prev) => ({ ...prev, otpCode: digits.join("") }));

          inputsRef.current[3].focus();
        })
        .catch(() => {});

      return () => ac.abort();
    }
  }, []);

  // ---------------------------------------------------
  // ⚡ OTP Input Handler
  // ---------------------------------------------------
  const handleInput = (e, index) => {
    const value = e.target.value.replace(/[^0-9]/g, "");

    e.target.value = value;

    // ویبره خفیف
    if (navigator.vibrate) navigator.vibrate(20);

    let newOtp = "";

    // پیش‌نویس کد جدید
    inputsRef.current.forEach((inp) => {
      newOtp += inp.value || "";
    });

    setOtpState((prev) => ({ ...prev, otpCode: newOtp }));

    // پر شد رفت به بعدی
    if (value && index < 3) {
      inputsRef.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !e.target.value && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  // ---------------------------------------------------
  // 🔐 Verify OTP
  // ---------------------------------------------------
  const verifyOTP = async () => {
    if (otpState.otpCode.length !== 4) {
      toast.warning("کد کامل نیست!");
      return;
    }

    api
      .post(`/${OTP_VERIFIED}`, otpState)
      .then((res) => {
        if (res.data.isSuccess) {
          toast.success(res.data.message);
          navigate("/login");
        } else {
          toast.warning(res.data.message);

          // Shake Animation Error
          setErrorShake(true);
          setTimeout(() => setErrorShake(false), 500);

          // پاک کردن کد
          inputsRef.current.forEach((el) => (el.value = ""));
          setOtpState((prev) => ({ ...prev, otpCode: "" }));
          inputsRef.current[0].focus();
        }
      })
      .catch((err) => console.log(err));
  };

  const continueHandler = (e) => {
    e.preventDefault();
    verifyOTP();
  };

  // ---------------------------------------------------
  // 🔁 Re-send OTP
  // ---------------------------------------------------
  const reSendOtp = () => {
    if (cooldown > 0) return;

    api
      .post(`/${OTP_SEND}`, reOtp)
      .then((res) => {
        if (res.data.isSuccess) {
          toast.success(res.data.message);
          setTimer();
        } else {
          toast.warning(res.data.message);
        }
      })
      .catch(() => {
        toast.warning("تا پایان زمان منتظر بمانید");
        setTimer();
      });
  };

  const reSendHandler = (e) => {
    e.preventDefault();
    reSendOtp();
  };

  // ---------------------------------------------------
  // ⏳ Timer
  // ---------------------------------------------------
  const setTimer = () => {
    setCooldown(180);
    const timer = setInterval(() => {
      setCooldown((p) => {
        if (p <= 1) clearInterval(timer);
        return p - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    setTimer();
  }, []);

  // ---------------------------------------------------
  // 🖼 UI
  // ---------------------------------------------------

  return (
    <div className="bg-light vh-100 d-flex justify-content-center align-items-center px-3">

      <div className="w-100" style={{ maxWidth: "420px" }}>

        <div className="text-center mb-4">
          <h2 className="fw-bold text-primary fs-4 mb-2">تأیید شماره همراه</h2>
          <p className="text-muted small">
            کد ۴ رقمی به شماره{" "}
            <span className="fw-bold">{values.mobileNumber}</span> ارسال شد
          </p>
        </div>

        {/* OTP Inputs */}
        <div
          className={`otp-wrapper d-flex gap-2 mb-4 ${errorShake ? "shake" : ""}`}
        >
          {[0, 1, 2, 3].map((i) => (
            <input
              key={i}
              ref={(el) => (inputsRef.current[i] = el)}
              type="text"
              maxLength="1"
              inputMode="numeric"
              className="otp-input"
              onChange={(e) => handleInput(e, i)}
              onKeyDown={(e) => handleKeyDown(e, i)}
            />
          ))}
        </div>

        <button
          className="btn btn-primary w-100 py-2 fw-bold rounded-3 mb-3"
          onClick={continueHandler}
        >
          تأیید و ادامه
        </button>

        <div className="d-flex justify-content-between">
          <button className="btn btn-outline-secondary rounded-3" onClick={prevStep}>
            اصلاح
          </button>

          <button
            onClick={reSendHandler}
            disabled={cooldown > 0}
            className={`btn ${
              cooldown > 0 ? "btn-outline-warning" : "btn-warning"
            } rounded-3`}
          >
            {cooldown > 0
              ? `ارسال مجدد (${cooldown} ثانیه)`
              : "ارسال مجدد کد"}
          </button>
        </div>
      </div>

      {/* Styles */}
      <style>{`
        .otp-wrapper {
          direction: ltr !important;        /* والد را LTR کن */
          display: flex !important;         /* مطمئن شو flex است */
          justify-content: center !important;
          flex-direction: row !important;   /* row واقعی */
        }
        
        .otp-wrapper input {
          direction: ltr !important;        /* input ها هم LTR */
          text-align: center !important;
          width: 55px;
          height: 58px;
          border-radius: 12px;
          border: 1px solid #ced4da;
          font-size: 1.6rem;
          letter-spacing: 0.5rem;
        }
        .otp-input {
          width: 55px;
          height: 58px;
          border-radius: 12px;
          border: 1px solid #ced4da;
          font-size: 1.6rem;
          letter-spacing: 0.5rem;
          text-align: center;
          direction: ltr;
        }
        @media (max-width: 480px) {
          .otp-input {
            width: 48px;
            height: 52px;
            font-size: 1.6rem;
          }
        }
        .otp-input:focus {
          outline: none;
          border-color: #0d6efd;
          box-shadow: 0 0 4px rgba(13,110,253,.35);
        }

        /* Shake Error Animation */
        .shake {
          animation: shakeAnim .4s ease;
        }
        @keyframes shakeAnim {
          0% { transform: translateX(0); }
          20% { transform: translateX(-5px); }
          40% { transform: translateX(5px); }
          60% { transform: translateX(-4px); }
          80% { transform: translateX(4px); }
          100% { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default SMSCode;
